/**
 * The learner's own handwriting for one line (#393; UX_EXPERIENCE §4.5): "is this what I wrote?" is judged against the
 * page, not memory.
 *
 * - **Only a viewBox.** The page image is drawn at its full size in the page's own pixels (as `feedback-page-image.tsx`
 *   does) and the SVG's `viewBox` selects what shows. No other scaling arithmetic.
 * - **An exact box** shows the region box plus a small margin, clamped to the page.
 * - **An approximate box** (the reader's coordinates were not plain pixels) is often shifted or too short, so a tight
 *   crop cuts the sentence or shows the wrong row (#607). It shows a full-width band instead: x from 0 to the page
 *   width, the box's y-range padded by the larger of twice its height and 6% of the page height, clamped to the page.
 *   The dashed outline stays ("an area, not an exact position"), so the learner sees where the reader thought the line was.
 * - **A line is shown whole, never panned (#660).** The line's box is drawn about 44 CSS px tall (scale capped at 1). A
 *   band wider than the card is split into as many stacked rows as needed, each row its own `<svg>` with its own viewBox
 *   over a consecutive slice of the band, overlapping by about 4% of a row so no letter is lost in a seam. Rows run in the
 *   handwriting's direction: a line with a Hebrew letter starts at the right, else at the left. A continuation row starts
 *   with a small chevron. The card is measured, so the rows reflow on resize and on a phone's rotation. There is no
 *   scroller and no scrollbar.
 * - **If the image does not load, nothing is drawn.** The card works without it; no error text.
 */

import { useEffect, useRef, useState } from "react";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { pageImageUrl } from "./feedback-page-image.js";
import { lineDir } from "./untrusted-text.js";

export interface PageSize {
  readonly widthPx: number;
  readonly heightPx: number;
}

export interface Bbox {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

export interface RegionCropProps extends PageSize {
  readonly pageId: string;
  readonly bbox: Bbox;
  readonly approximate: boolean;
  readonly lineNumber: number;
  /** The reading's text, only to tell which way the handwriting runs (Hebrew: from the right). Absent: from the left. */
  readonly text?: string | undefined;
  /** How tall the line's own box is drawn, in CSS px. Default 44 (#641); the glance uses a smaller one. */
  readonly boxHeightPx?: number;
  /**
   * A feedback block, drawn as one picture that fills the card's width (#673): larger, up to `FILL_MAX_ENLARGE` times its own display scale so a very short
   * line is not blown up, and smaller when it is wider than the card. A block of several lines is never split into rows: that would cut every line in two.
   * Only the drawn size changes: the viewBox, and so the ink it shows, is the same.
   */
  readonly fill?: boolean;
}

/** How much `fill` may enlarge a block beyond its own display scale. */
export const FILL_MAX_ENLARGE = 2.5;

/** The scale of a block that fills `availablePx` with a viewBox `viewWidth` wide: whatever fits the card, at most FILL_MAX_ENLARGE times the base. */
export function filledScale(base: number, viewWidth: number, availablePx: number): number {
  if (availablePx <= 0 || viewWidth <= 0) return base;
  return Math.min(availablePx / viewWidth, base * FILL_MAX_ENLARGE);
}

/**
 * Exact: the region box padded by 1.5% of the page width, clamped. Approximate (#607): a full-width band, the box's
 * y-range padded by max(2 x box height, 6% of page height), clamped to the page.
 */
export function cropViewBox(
  bbox: Bbox,
  approximate: boolean,
  { widthPx, heightPx }: PageSize,
): { x: number; y: number; width: number; height: number } {
  if (approximate) {
    const pad = Math.max(2 * (bbox.y1 - bbox.y0), Math.round(heightPx * 0.06));
    const top = Math.max(0, bbox.y0 - pad);
    const bottom = Math.min(heightPx, bbox.y1 + pad);
    return { x: 0, y: top, width: widthPx, height: bottom - top };
  }
  const margin = Math.round(widthPx * 0.015);
  const x = Math.max(0, bbox.x0 - margin);
  const y = Math.max(0, bbox.y0 - margin);
  const right = Math.min(widthPx, bbox.x1 + margin);
  const bottom = Math.min(heightPx, bbox.y1 + margin);
  return { x, y, width: right - x, height: bottom - y };
}

/** Target on-screen height of the line's own box, in CSS px. */
const TARGET_BOX_HEIGHT_PX = 44;

/** Display scale: the box about `target` px tall, never enlarged past the page's own pixels. */
export function displayScale(
  bbox: Bbox,
  target: number = TARGET_BOX_HEIGHT_PX,
): number {
  const h = bbox.y1 - bbox.y0;
  return Math.min(1, target / (h > 0 ? h : 1));
}

/** How much neighbouring rows share, as a fraction of one row, so a letter on a seam is whole in at least one of them. */
const ROW_OVERLAP = 0.04;

/** Room kept at a row's start for the continuation chevron, in CSS px. */
export const CHEVRON_GUTTER_PX = 30;

/**
 * The slices of a band, in reading order. `availablePx` is the width one row may take; `scale` is CSS px per page px.
 * A band that fits is one slice. Otherwise every slice is a full row wide, they overlap by ROW_OVERLAP, together cover the
 * band from its first pixel to its last, and a Hebrew line (`rtl`) lists the rightmost slice first.
 */
export function bandRows(
  band: { readonly x: number; readonly width: number },
  scale: number,
  availablePx: number,
  rtl: boolean,
): { readonly x: number; readonly width: number }[] {
  const rowWidth =
    availablePx > 0 && scale > 0 ? availablePx / scale : band.width;
  if (!(band.width > rowWidth * 1.0001))
    return [{ x: band.x, width: band.width }];
  const step = rowWidth * (1 - ROW_OVERLAP);
  const count = 1 + Math.ceil((band.width - rowWidth) / step);
  const gap = (band.width - rowWidth) / (count - 1);
  const rows = Array.from({ length: count }, (_, i) => ({
    x: band.x + i * gap,
    width: rowWidth,
  }));
  return rtl ? rows.reverse() : rows;
}

/** The width of an element, kept current: the first reading at once, then whenever the layout changes. */
function useMeasuredWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const read = (): void => setWidth(Math.floor(el.clientWidth));
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function Chevron(): React.JSX.Element {
  return (
    <svg
      className="region-crop-chevron"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 5v6a3 3 0 0 0 3 3h11" />
      <path d="M15 10l4 4-4 4" />
    </svg>
  );
}

export function RegionCrop({
  pageId,
  bbox,
  approximate,
  lineNumber,
  widthPx,
  heightPx,
  text,
  boxHeightPx,
  fill = false,
}: RegionCropProps): React.JSX.Element | null {
  const t = useHomeworkLabels();
  const [failed, setFailed] = useState(false);
  const holder = useRef<HTMLDivElement>(null);
  const available = useMeasuredWidth(holder);
  if (failed || widthPx <= 0 || heightPx <= 0) return null;
  const { x, y, width, height } = cropViewBox(bbox, approximate, {
    widthPx,
    heightPx,
  });
  if (width <= 0 || height <= 0) return null;
  const base = displayScale(bbox, boxHeightPx);
  const scale = fill ? filledScale(base, width, available) : base;
  const rtl = text !== undefined && lineDir(text) === "rtl";
  // Not measured yet: one row at the picture's own size, narrowed to the card by CSS, so nothing is lost meanwhile.
  const fitsWhole = available <= 0 || width * scale <= available * 1.0001;
  // Rows of a split band reserve room for the chevron, so they are all the same width.
  const rows = fitsWhole
    ? [{ x, width }]
    : bandRows({ x, width }, scale, available - CHEVRON_GUTTER_PX, rtl);
  const dash = Math.max(6, Math.round(widthPx / 150));
  const many = rows.length > 1;
  return (
    <div className="region-crop-frame" dir="ltr">
      <div
        className="region-crop-rows"
        ref={holder}
        role="img"
        aria-label={t.cropLabel(lineNumber)}
      >
        {rows.map((row, index) => (
          <div
            className="region-crop-row"
            key={`${String(index)}-${String(row.x)}`}
            dir={rtl ? "rtl" : "ltr"}
          >
            {many ? (
              index > 0 ? (
                <Chevron />
              ) : (
                <span className="region-crop-gutter" aria-hidden="true" />
              )
            ) : null}
            <svg
              className="region-crop"
              width={Math.round(row.width * scale)}
              height={Math.round(height * scale)}
              viewBox={`${String(row.x)} ${String(y)} ${String(row.width)} ${String(height)}`}
              aria-hidden="true"
              focusable="false"
            >
              <image
                href={pageImageUrl(pageId)}
                x={0}
                y={0}
                width={widthPx}
                height={heightPx}
                onError={() => setFailed(true)}
              />
              {approximate ? (
                <rect
                  x={bbox.x0}
                  y={bbox.y0}
                  width={bbox.x1 - bbox.x0}
                  height={bbox.y1 - bbox.y0}
                  strokeDasharray={`${String(dash)} ${String(dash)}`}
                />
              ) : null}
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}
